import { EventMsgError } from "./event-msg-error.js";

/**
 * Error for input validation failures
 */
export class ValidationError extends EventMsgError {
  /** Field that failed validation */
  readonly field: string | undefined;

  /** Value that was invalid */
  readonly value: unknown;

  constructor(
    message: string,
    options: {
      field?: string;
      value?: unknown;
      context?: Record<string, unknown>;
      solutions?: string[];
      cause?: Error;
    } = {}
  ) {
    super(message, "VALIDATION_ERROR", {
      ...options,
      context: {
        ...options.context,
        field: options.field,
        value: options.value,
      },
      solutions: [
        "Check input parameters",
        "Verify value ranges and types",
        "Review API documentation",
      ],
    });

    this.field = options.field;
    this.value = options.value;
  }
}

/**
 * Address validation error (0-255 range)
 */
export class AddressValidationError extends ValidationError {
  constructor(
    address: number,
    field = "address",
    options: {
      context?: Record<string, unknown>;
      solutions?: string[];
      cause?: Error;
    } = {}
  ) {
    super(`Invalid ${field}: ${address}. Must be 0-255`, {
      ...options,
      field,
      value: address,
      solutions: [
        "Use address in range 0-255",
        "Check for negative values",
        "Verify address is an integer",
      ],
    });

    this.code = "ADDRESS_VALIDATION_ERROR";
  }
}

/**
 * Message size validation error
 */
export class MessageSizeError extends ValidationError {
  constructor(
    size: number,
    maxSize: number,
    field = "message",
    options: {
      context?: Record<string, unknown>;
      solutions?: string[];
      cause?: Error;
    } = {}
  ) {
    super(`${field} size ${size} bytes exceeds maximum ${maxSize} bytes`, {
      ...options,
      field,
      value: size,
      context: {
        ...options.context,
        maxSize,
      },
      solutions: [
        `Reduce ${field} size to ${maxSize} bytes or less`,
        "Consider splitting large messages",
        "Use more efficient encoding",
      ],
    });

    this.code = "MESSAGE_SIZE_ERROR";
  }
}
